import axios from '../config/axios';

const API_URL = `${process.env.REACT_APP_API_URL}/api/section-validation`;

/**
 * Universal Section Validation and Regeneration Service
 * 
 * This service handles the validation and regeneration workflow for any section
 * to ensure generated content follows the specified structure and format requirements.
 */

/**
 * Validate any section content against requirements
 * @param {string} sectionTitle - The title of the section (e.g., "1. PROTOCOL SUMMARY")
 * @param {string} generatedContent - The generated section content
 * @param {string} sectionPrompt - The original section prompt with requirements
 * @param {string} context - Optional context data
 * @returns {Promise<Object>} - Validation result
 */
export const validateSection = async (sectionTitle, generatedContent, sectionPrompt, context = '') => {
    try {
        console.log(`🔍 Validating ${sectionTitle} content...`);
        
        const response = await axios.post(`${API_URL}/validate`, {
            sectionTitle,
            generatedContent,
            sectionPrompt,
            context
        });

        if (response.data.success) {
            console.log(`✅ ${sectionTitle} validation completed`);
            return response.data.validationResult;
        } else {
            throw new Error(response.data.message || 'Validation failed');
        }
    } catch (error) {
        console.error(`❌ Error validating ${sectionTitle}:`, error);
        throw new Error(`${sectionTitle} validation failed: ${error.response?.data?.message || error.message}`);
    }
};

/**
 * Generate error report for section validation failures
 * @param {string} sectionTitle - The title of the section
 * @param {Object} validationResult - The validation result from validateSection
 * @param {string} originalPrompt - The original section prompt
 * @returns {Promise<string>} - Detailed error report
 */
export const generateErrorReport = async (sectionTitle, validationResult, originalPrompt) => {
    try {
        console.log(`📝 Generating error report for ${sectionTitle}...`);
        
        const response = await axios.post(`${API_URL}/generate-error-report`, {
            sectionTitle,
            validationResult,
            originalPrompt
        });

        if (response.data.success) {
            console.log(`✅ Error report generated successfully for ${sectionTitle}`);
            return response.data.errorReport;
        } else {
            throw new Error(response.data.message || 'Error report generation failed');
        }
    } catch (error) {
        console.error(`❌ Error generating error report for ${sectionTitle}:`, error);
        throw new Error(`Error report generation failed: ${error.response?.data?.message || error.message}`);
    }
};

/**
 * Regenerate section content based on error report
 * @param {string} sectionTitle - The title of the section
 * @param {string} originalPrompt - The original section prompt
 * @param {string} context - Context data
 * @param {string} errorReport - Detailed error report
 * @param {number} attemptNumber - Regeneration attempt number
 * @returns {Promise<string>} - Regenerated content
 */
export const regenerateSection = async (sectionTitle, originalPrompt, context, errorReport, attemptNumber = 1) => {
    try {
        console.log(`🔄 Regenerating ${sectionTitle} (attempt ${attemptNumber})...`);
        
        const response = await axios.post(`${API_URL}/regenerate`, {
            sectionTitle,
            originalPrompt,
            context,
            errorReport,
            attemptNumber
        });

        if (response.data.success) {
            console.log(`✅ ${sectionTitle} regenerated successfully (attempt ${attemptNumber})`);
            return response.data.regeneratedContent;
        } else {
            throw new Error(response.data.message || 'Regeneration failed');
        }
    } catch (error) {
        console.error(`❌ Error regenerating ${sectionTitle} (attempt ${attemptNumber}):`, error);
        throw new Error(`${sectionTitle} regeneration failed: ${error.response?.data?.message || error.message}`);
    }
};

/**
 * Complete validation and regeneration workflow for any section
 * @param {string} sectionTitle - The title of the section
 * @param {string} generatedContent - The initially generated content
 * @param {string} sectionPrompt - The original section prompt
 * @param {string} context - Optional context data
 * @param {number} maxAttempts - Maximum regeneration attempts (default: 3)
 * @param {string} projectId - Optional project ID to fetch fresh clinical intake data
 * @param {Object} options - Optional { source: 'usdm', studyData }: server skips Clinical Intake and accepts
 *   "Not yet defined in Study Design" wording instead of forcing invented content. studyData must be
 *   the same structured study JSON sent to generateContent, so the validator sees what the generator saw.
 * @returns {Promise<Object>} - Complete workflow result
 */
export const validateAndRegenerateSection = async (sectionTitle, generatedContent, sectionPrompt, context = '', maxAttempts = 3, projectId = null, options = {}) => {
    try {
        console.log(`🚀 Starting complete ${sectionTitle} validation and regeneration workflow...`);
        if (projectId) {
            console.log(`📋 Using project ID ${projectId} for fresh clinical intake data`);
        }
        
        const response = await axios.post(`${API_URL}/validate-and-regenerate`, {
            sectionTitle,
            generatedContent,
            sectionPrompt,
            context,
            maxAttempts,
            projectId,
            source: options?.source || null,
            studyData: options?.studyData || null
        });

        if (response.data.success) {
            console.log(`✅ Complete ${sectionTitle} workflow completed`);
            return response.data.result;
        } else {
            throw new Error(response.data.message || 'Complete workflow failed');
        }
    } catch (error) {
        console.error(`❌ Error in complete ${sectionTitle} workflow:`, error);
        throw new Error(`Complete ${sectionTitle} workflow failed: ${error.response?.data?.message || error.message}`);
    }
};

/**
 * Get validation history for a specific section
 * @param {string} sectionId - The section identifier
 * @returns {Promise<Object>} - Validation history
 */
export const getValidationHistory = async (sectionId) => {
    try {
        console.log(`📋 Fetching validation history for section ${sectionId}...`);
        
        const response = await axios.get(`${API_URL}/history/${sectionId}`);

        if (response.data.success) {
            console.log('✅ Validation history fetched successfully');
            return response.data;
        } else {
            throw new Error(response.data.message || 'Failed to fetch validation history');
        }
    } catch (error) {
        console.error('❌ Error fetching validation history:', error);
        throw new Error(`Failed to fetch validation history: ${error.response?.data?.message || error.message}`);
    }
};

/**
 * Enhanced section generation with validation and regeneration
 * This is a convenience function that combines generation with validation
 * @param {string} sectionTitle - The title of the section
 * @param {string} sectionPrompt - The original section prompt
 * @param {string} context - Context data
 * @param {string} initialContent - Initially generated content (optional)
 * @param {number} maxAttempts - Maximum regeneration attempts
 * @returns {Promise<Object>} - Final result with validated content
 */
export const generateValidatedSection = async (sectionTitle, sectionPrompt, context, initialContent = '', maxAttempts = 3) => {
    try {
        console.log(`🎯 Starting enhanced ${sectionTitle} generation with validation...`);
        
        let currentContent = initialContent;
        
        // If no initial content provided, we would need to generate it first
        // This would typically call the AI generation service
        if (!currentContent) {
            console.log(`⚠️ No initial content provided for ${sectionTitle} - would need to generate first`);
            throw new Error('Initial content is required for validation workflow');
        }
        
        // Perform validation and regeneration workflow
        const result = await validateAndRegenerateSection(
            sectionTitle,
            currentContent,
            sectionPrompt,
            context,
            maxAttempts
        );
        
        console.log(`✅ Enhanced ${sectionTitle} generation completed`);
        return result;
        
    } catch (error) {
        console.error(`❌ Error in enhanced ${sectionTitle} generation:`, error);
        throw new Error(`Enhanced ${sectionTitle} generation failed: ${error.message}`);
    }
};



